import { useCallback, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { Box, Typography } from 'components/common';
import { calendarUtils } from 'components/calendar/utils';
import { useCalendar } from 'components/calendar/context';
import { FilterForm, SubmittedValues } from './filter-form';
import { CalendarNavigation } from '../../shared/navigation';
import { AgendaGrid } from './grid';

const calculateDatesFromOption = (
	filterOption: string,
	timezone: string,
	displayed?: DateTime
): { startDate: DateTime; endDate: DateTime } => {
	const currentDate = DateTime.local({ zone: timezone });
	const dateToUse = displayed || currentDate;

	switch (filterOption) {
		case 'monthly':
			return {
				startDate: dateToUse.startOf('month'),
				endDate: dateToUse.endOf('month'),
			};
		case 'weekly':
			return {
				startDate: calendarUtils.getFirstDayOfWeek(dateToUse),
				endDate: calendarUtils.getLastDayOfWeek(dateToUse),
			};
		case 'next_7_days':
			// Always use current date for "next X days" options
			return {
				startDate: currentDate.startOf('day'),
				endDate: currentDate.startOf('day').plus({ day: 7 }),
			};
		case 'next_30_days':
			// Always use current date for "next X days" options
			return {
				startDate: currentDate.startOf('day'),
				endDate: currentDate.startOf('day').plus({ day: 30 }),
			};
		case 'next_90_days':
			// Always use current date for "next X days" options
			return {
				startDate: currentDate.startOf('day'),
				endDate: currentDate.startOf('day').plus({ day: 90 }),
			};
		default:
			// Default to monthly if option is not recognized
			return {
				startDate: dateToUse.startOf('month'),
				endDate: dateToUse.endOf('month'),
			};
	}
};

export const Agenda = () => {
	const {
		displayed,
		setDisplayed,
		formatDate,
		loadEvents,
		timezone,
		agendaFilterOption,
		agendaCustomDates,
		setAgendaFilterOption,
		setAgendaCustomDates,
	} = useCalendar();

	const filterOption = agendaFilterOption || 'monthly';

	const dates = useMemo(() => {
		if (filterOption === 'custom-range' && agendaCustomDates) {
			return {
				startDate: agendaCustomDates.startDate,
				endDate: agendaCustomDates.endDate,
			};
		}
		return calculateDatesFromOption(filterOption, timezone, displayed);
	}, [filterOption, agendaCustomDates, timezone, displayed]);

	const filterValues: SubmittedValues = useMemo(
		() => ({ filterOption, dates }),
		[filterOption, dates]
	);

	const handleOnSubmit = useCallback(
		({
			filterOption: newFilterOption,
			dates: newDates,
		}: SubmittedValues) => {
			if (newFilterOption !== 'custom') {
				setAgendaFilterOption(newFilterOption);
				if (newFilterOption === 'custom-range') {
					setAgendaCustomDates(newDates);
				} else {
					setAgendaCustomDates(undefined);
				}
			}
		},
		[setAgendaFilterOption, setAgendaCustomDates]
	);

	const handleOnPressNext = useCallback(() => {
		if (filterOption === 'monthly') {
			setDisplayed(displayed.plus({ month: 1 }));
		} else if (filterOption === 'weekly') {
			setDisplayed(displayed.plus({ week: 1 }));
		}
	}, [displayed, filterOption, setDisplayed]);

	const handleOnPressPrev = useCallback(() => {
		if (filterOption === 'monthly') {
			setDisplayed(displayed.minus({ month: 1 }));
		} else {
			setDisplayed(displayed.minus({ week: 1 }));
		}
	}, [displayed, filterOption, setDisplayed]);

	const renderNavigationDisplay = useCallback(() => {
		if (filterOption === 'monthly') {
			return formatDate(displayed, 'MMMM yyyy');
		}

		const startDate = calendarUtils.getFirstDayOfWeek(displayed);
		const endDate = calendarUtils.getLastDayOfWeek(displayed);

		return `${formatDate(startDate, 'MMMM d')} - ${formatDate(
			endDate,
			'MMMM d'
		)}`;
	}, [filterOption, displayed, formatDate]);

	useEffect(() => {
		loadEvents(dates);
	}, [dates, loadEvents]);

	return (
		<>
			<Box paddingTop={3}>
				<Box paddingX={2}>
					<Typography variant='button2' fontSize={20}>
						Agenda View
					</Typography>
					<Box display='flex' marginTop={2}>
						<FilterForm
							initialValues={filterValues}
							onSubmit={handleOnSubmit}
							timezone={timezone}
						/>
					</Box>
				</Box>
				{['monthly', 'weekly'].includes(filterOption) ? (
					<CalendarNavigation
						display={renderNavigationDisplay()}
						onNext={handleOnPressNext}
						onPrev={handleOnPressPrev}
					/>
				) : null}
			</Box>
			<Box>
				<AgendaGrid {...dates} />
			</Box>
		</>
	);
};
